// Chat — persistent E2EE messaging (Layer CH, chat-mls.md CH3). The page
// wears the app's own clothes: the People-page header, search and row
// language, one bordered card holding the two panes. Everything rendered
// here was decrypted ON THIS DEVICE; the server only ever saw envelopes.

import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router';

import { api, type DirectoryEntry } from '../api';
import {
  closeConversation,
  downloadFile,
  ensureReady,
  openConversation,
  registerImageCacheSetter,
  sendFile,
  sendMessage,
  startDM,
  sync,
  toggleReaction,
} from '../chat/chatClient';
import { useShell } from '../components/AppShell';
import { BrizinaLoader } from '../components/BrizinaLoader';
import { pushToast } from '../store/callStore';
import { splitLinks } from '../chat/linkify';
import { crossesDay, dayLabel } from '../chat/time';
import { useChatStore, type ChatConvView, type ChatMessageView } from '../store/chatStore';
import { Avatar } from '../ui/Avatar';
import { IconButton } from '../ui/IconButton';
import {
  IconChat,
  IconChevronDown,
  IconCopy,
  IconDownload,
  IconFile,
  IconFiles,
  IconImage,
  IconLock,
  IconPlus,
  IconSearch,
  IconSend,
  IconSmile,
} from '../ui/icons';

export function ChatPage() {
  const { me } = useShell();
  const ready = useChatStore((s) => s.ready);
  const failed = useChatStore((s) => s.failed);
  const meId = me?.id;

  useEffect(() => {
    if (!meId) return;
    void ensureReady(meId).then((ok) => {
      if (ok) void sync();
    });
    // Pings are the fast path, not the only path: a missed socket frame must
    // cost seconds, never a stuck conversation.
    const tick = window.setInterval(() => void sync(), 45_000);
    const onFocus = () => void sync();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.clearInterval(tick);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      closeConversation();
    };
  }, [meId]);

  if (!me) return <Navigate to="/" replace />;
  return (
    <div className="bz-chatshell">
      <header className="bz-people-header">
        <div className="bz-people-head-left">
          <h1>Discussions</h1>
        </div>
        <span className="bz-chat-e2ee">
          <IconLock size={13} />
          End-to-end encrypted
        </span>
      </header>
      {failed && (
        <p className="bz-people-note bz-error-text">
          Chat could not start on this browser. Reload to try again.
        </p>
      )}
      {!failed && !ready && (
        <div className="bz-chat-loading">
          <BrizinaLoader />
          <p className="bz-people-note">Setting up encrypted chat…</p>
        </div>
      )}
      {!failed && ready && <ChatCard myId={me.id} />}
    </div>
  );
}

function ChatCard({ myId }: { myId: string }) {
  const convs = useChatStore((s) => s.convs);
  const openId = useChatStore((s) => s.openConvId);
  const [people, setPeople] = useState<DirectoryEntry[]>([]);
  const [picking, setPicking] = useState(false);

  // The directory names DM peers and feeds the new-chat picker.
  useEffect(() => {
    api
      .directory()
      .then(setPeople)
      .catch(() => undefined);
  }, []);

  const personOf = useCallback(
    (userId?: string) => people.find((p) => p.id === userId),
    [people],
  );

  const openDM = useCallback((peer: DirectoryEntry) => {
    setPicking(false);
    void (async () => {
      try {
        const id = await startDM(peer.id);
        await openConversation(id);
      } catch (err) {
        pushToast(err instanceof Error ? err.message : 'Could not start the chat.', 'warning');
      }
    })();
  }, []);
  const togglePicker = useCallback(() => setPicking((v) => !v), []);
  const openConv = useCallback((id: string) => {
    setPicking(false);
    void openConversation(id);
  }, []);

  const open = convs.find((c) => c.id === openId);
  const openPeer = personOf(open?.peerUserId);

  return (
    <div className={openId ? 'bz-chatcard bz-chatcard-open' : 'bz-chatcard'}>
      <aside className="bz-chat-side">
        <div className="bz-chat-side-head">
          <span className="bz-chat-side-title">Conversations</span>
          <IconButton shape="round" label="New discussion" onClick={togglePicker} className="bz-people-new">
            <IconPlus size={17} />
          </IconButton>
        </div>
        {picking ? (
          <Picker myId={myId} people={people} onPick={openDM} onCancel={togglePicker} />
        ) : (
          <div className="bz-chat-side-scroll">
            {convs.length === 0 && (
              <div className="bz-chat-blank">
                <IconChat size={26} />
                <p>No conversations yet.</p>
                <p className="bz-chat-blank-sub">Start one with the + above.</p>
              </div>
            )}
            {convs.map((c) => (
              <ConvRow
                key={c.id}
                conv={c}
                person={personOf(c.peerUserId)}
                active={c.id === openId}
                onOpen={openConv}
              />
            ))}
          </div>
        )}
      </aside>
      <section className="bz-chat-main">
        {open ? (
          <Thread
            convId={open.id}
            name={openPeer?.name ?? 'Unknown'}
            peerId={open.peerUserId ?? open.id}
            peerAvatar={openPeer?.avatar}
            writable={!open.removed}
            pending={open.pending}
          />
        ) : (
          <div className="bz-chat-blank bz-chat-blank-main">
            <IconChat size={34} />
            <p>Your messages are end-to-end encrypted.</p>
            <p className="bz-chat-blank-sub">
              Not even the server can read them. Pick a conversation, or start one.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}

function Picker({
  myId,
  people,
  onPick,
  onCancel,
}: {
  myId: string;
  people: DirectoryEntry[];
  onPick: (p: DirectoryEntry) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = useState('');
  const onQ = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value), []);
  const filtered = useMemo(
    () =>
      people.filter(
        (p) =>
          p.id !== myId &&
          (!q.trim() || p.name.toLowerCase().includes(q.trim().toLowerCase())),
      ),
    [people, myId, q],
  );
  return (
    <>
      <div className="bz-chat-picker-head">
        <span className="bz-chat-picker-title">New discussion</span>
        <button type="button" className="bz-chat-picker-cancel" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <div className="bz-chat-side-search">
        <div className="bz-people-search">
          <IconSearch size={16} />
          <input
            type="search"
            placeholder="Search people"
            value={q}
            onChange={onQ}
            aria-label="Search people"
          />
        </div>
      </div>
      <div className="bz-chat-side-scroll">
        {filtered.length === 0 && <p className="bz-people-note bz-chat-side-note">No one matches.</p>}
        {filtered.map((p) => (
          <PickRow key={p.id} person={p} onPick={onPick} />
        ))}
      </div>
    </>
  );
}

const PickRow = memo(function PickRow({
  person,
  onPick,
}: {
  person: DirectoryEntry;
  onPick: (p: DirectoryEntry) => void;
}) {
  const click = useCallback(() => onPick(person), [onPick, person]);
  return (
    <button type="button" className="bz-chat-row" onClick={click}>
      <Avatar name={person.name} identity={person.id} size={38} photoUrl={person.avatar} />
      <span className="bz-chat-row-body">
        <span className="bz-chat-row-name">{person.name}</span>
        {person.email && <span className="bz-chat-row-last">{person.email}</span>}
      </span>
    </button>
  );
});

function rowTime(at?: number): string {
  if (!at) return '';
  const d = new Date(at);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

const ConvRow = memo(function ConvRow({
  conv,
  person,
  active,
  onOpen,
}: {
  conv: ChatConvView;
  person?: DirectoryEntry;
  active: boolean;
  onOpen: (id: string) => void;
}) {
  const click = useCallback(() => onOpen(conv.id), [onOpen, conv.id]);
  const name = person?.name ?? 'Unknown';
  return (
    <button
      type="button"
      className={active ? 'bz-chat-row bz-chat-row-on' : 'bz-chat-row'}
      onClick={click}
    >
      <Avatar name={name} identity={conv.peerUserId ?? conv.id} size={42} photoUrl={person?.avatar} />
      <span className="bz-chat-row-body">
        <span className="bz-chat-row-top">
          <span className="bz-chat-row-name">{name}</span>
          <span className="bz-chat-row-time">{rowTime(conv.lastAt)}</span>
        </span>
        <span className="bz-chat-row-bottom">
          <span className="bz-chat-row-last">
            {conv.pending ? (
              <span className="bz-chat-dots" role="status" aria-label="Connecting">
                <i />
                <i />
                <i />
              </span>
            ) : (
              (conv.lastText ?? 'Say hello')
            )}
          </span>
          {conv.unread > 0 && (
            <span className="bz-chat-unread">{conv.unread > 9 ? '9+' : conv.unread}</span>
          )}
        </span>
      </span>
    </button>
  );
});

// The composer's quick palette: the emoji people actually send (Unicode
// frequency data, workplace-leaning), not a thousand-entry browser.
const QUICK_EMOJI = [
  '👍', '❤️', '😂', '🎉', '🙏', '😊', '🔥', '👏', '😍', '🤣',
  '😅', '🥳', '😎', '🤔', '🙌', '💪', '✅', '👌', '😉', '🥺',
  '😢', '😭', '🤯', '😴', '🚀', '⭐', '☕', '🎂',
] as const;

const EmojiKey = memo(function EmojiKey({
  emoji,
  onPick,
}: {
  emoji: string;
  onPick: (e: string) => void;
}) {
  const click = useCallback(() => onPick(emoji), [onPick, emoji]);
  return (
    <button type="button" className="bz-chat-emoji-key" onClick={click} aria-label={`Insert ${emoji}`}>
      {emoji}
    </button>
  );
});

const REACTION_SET = ['👍', '❤️', '😂', '🎉', '😮', '🙏'] as const;

/** Message text with URLs as real links — segments through React's
 * escaping, http(s) only (linkify.ts), never raw HTML. */
const Linkified = memo(function Linkified({ text }: { text: string }) {
  const segments = useMemo(() => splitLinks(text), [text]);
  return (
    <>
      {segments.map((seg, i) =>
        seg.kind === 'link' ? (
          <a key={i} href={seg.href} target="_blank" rel="noopener noreferrer" className="bz-chat-link">
            {seg.value}
          </a>
        ) : (
          <Fragment key={i}>{seg.value}</Fragment>
        ),
      )}
    </>
  );
});

const ReactionChip = memo(function ReactionChip({
  convId,
  seq,
  emoji,
  count,
  mine,
}: {
  convId: string;
  seq: number;
  emoji: string;
  count: number;
  mine: boolean;
}) {
  const click = useCallback(() => void toggleReaction(convId, seq, emoji), [convId, seq, emoji]);
  return (
    <button
      type="button"
      className={mine ? 'bz-chat-reaction bz-chat-reaction-mine' : 'bz-chat-reaction'}
      onClick={click}
      aria-label={`${emoji} ${count}${mine ? ', including you' : ''}`}
    >
      {emoji} {count}
    </button>
  );
});

const ReactKey = memo(function ReactKey({
  convId,
  seq,
  emoji,
  onDone,
}: {
  convId: string;
  seq: number;
  emoji: string;
  onDone: () => void;
}) {
  const click = useCallback(() => {
    void toggleReaction(convId, seq, emoji);
    onDone();
  }, [convId, seq, emoji, onDone]);
  return (
    <button type="button" className="bz-chat-emoji-key" onClick={click} aria-label={`React ${emoji}`}>
      {emoji}
    </button>
  );
});

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Decrypted image blob URLs, cached per object key so scrolling a thread
// doesn't re-fetch or re-decrypt. Held for the session (chat scale).
const imageUrlCache = new Map<string, string>();
registerImageCacheSetter((k, u) => imageUrlCache.set(k, u));

const ImageBubble = memo(function ImageBubble({
  convId,
  file,
  localUrl,
  sending,
  progress,
}: {
  convId: string;
  file: NonNullable<ChatMessageView['file']>;
  localUrl?: string;
  sending?: boolean;
  progress?: number;
}) {
  const [src, setSrc] = useState<string | null>(
    () => localUrl ?? imageUrlCache.get(file.objectKey) ?? null,
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (src || failed || sending || !file.objectKey) return;
    let cancelled = false;
    void downloadFile(convId, file)
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        imageUrlCache.set(file.objectKey, url);
        setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [convId, file, src, failed, sending]);

  if (failed) return <FileCard file={file} mine={false} />;
  if (!src) {
    return (
      <span className="bz-chat-image bz-chat-image-loading">
        <span className="bz-chat-image-spin" aria-label="Loading image" />
      </span>
    );
  }
  return (
    <span className="bz-chat-image">
      <img src={src} alt={file.name} loading="lazy" />
      {sending && (
        <span className="bz-chat-media-overlay">
          <ProgressRing value={progress ?? 0} />
        </span>
      )}
    </span>
  );
});

function ProgressRing({ value }: { value: number }) {
  const pct = Math.round(Math.max(0, Math.min(1, value)) * 100);
  return (
    <span className="bz-chat-ring" role="progressbar" aria-valuenow={pct}>
      <span className="bz-chat-ring-spin" />
      <span className="bz-chat-ring-pct">{pct}%</span>
    </span>
  );
}

const FileCard = memo(function FileCard({
  file,
  mine,
  sending,
  progress,
  onSave,
  downloading,
  downloadProgress,
}: {
  file: NonNullable<ChatMessageView['file']>;
  mine: boolean;
  sending?: boolean;
  progress?: number;
  onSave?: () => void;
  downloading?: boolean;
  downloadProgress?: number;
}) {
  const busy = sending || downloading;
  return (
    <button
      type="button"
      className={mine ? 'bz-chat-file bz-chat-file-mine' : 'bz-chat-file'}
      onClick={onSave}
      disabled={busy || sending}
      title={sending ? 'Sending…' : `Download ${file.name}`}
    >
      <span className="bz-chat-file-icon">
        {busy ? <ProgressRing value={(sending ? progress : downloadProgress) ?? 0} /> : <IconFile size={20} />}
      </span>
      <span className="bz-chat-file-body">
        <span className="bz-chat-file-name">{file.name}</span>
        <span className="bz-chat-file-size">{humanSize(file.size)}</span>
      </span>
      {!busy && (
        <span className="bz-chat-file-dl">
          <IconDownload size={16} />
        </span>
      )}
    </button>
  );
});

async function imageBlobFor(convId: string, file: NonNullable<ChatMessageView['file']>, localUrl?: string): Promise<Blob> {
  const cached = localUrl ?? imageUrlCache.get(file.objectKey);
  if (cached) return (await fetch(cached)).blob();
  return downloadFile(convId, file);
}

const MessageRow = memo(function MessageRow({
  convId,
  m,
  pickerOpen,
  onPicker,
}: {
  convId: string;
  m: ChatMessageView;
  pickerOpen: boolean;
  onPicker: (seq: number | null) => void;
}) {
  const togglePicker = useCallback(
    () => onPicker(pickerOpen ? null : m.seq),
    [onPicker, pickerOpen, m.seq],
  );
  const closePicker = useCallback(() => onPicker(null), [onPicker]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dl, setDl] = useState<number | null>(null);
  const isImage = !!m.file && m.file.mime.startsWith('image/');
  const sending = m.status === 'sending';

  const toggleMenu = useCallback(() => setMenuOpen((v) => !v), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const save = useCallback(() => {
    const file = m.file;
    if (!file) return;
    closeMenu();
    // An image already on screen (or just sent) saves from its blob with no
    // re-download; a file downloads with a progress loader.
    if (isImage) {
      void imageBlobFor(convId, file, m.localUrl)
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.name;
          a.click();
          URL.revokeObjectURL(url);
        })
        .catch(() => pushToast('Could not save — the file may have expired.', 'warning'));
      return;
    }
    setDl(0);
    void downloadFile(convId, file, setDl)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.name;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => pushToast('Could not download — the file may have expired.', 'warning'))
      .finally(() => setDl(null));
  }, [convId, m.file, m.localUrl, isImage, closeMenu]);

  const copy = useCallback(() => {
    const file = m.file;
    if (!file) return;
    closeMenu();
    void imageBlobFor(convId, file, m.localUrl)
      .then(
        (blob) =>
          new Promise<Blob>((resolve, reject) => {
            // Clipboard reliably takes PNG; transcode the JPEG.
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.naturalWidth;
              canvas.height = img.naturalHeight;
              const ctx = canvas.getContext('2d');
              if (!ctx) return reject(new Error('no canvas'));
              ctx.drawImage(img, 0, 0);
              canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode'))), 'image/png');
            };
            img.onerror = () => reject(new Error('decode'));
            img.src = URL.createObjectURL(blob);
          }),
      )
      .then((png) => navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]))
      .then(() => pushToast('Image copied', 'info'))
      .catch(() => pushToast('Could not copy the image.', 'warning'));
  }, [convId, m.file, m.localUrl, closeMenu]);

  return (
    <div className={m.isMine ? 'bz-chat-msg bz-chat-msg-mine' : 'bz-chat-msg'}>
      <span className="bz-chat-bubble-wrap">
        <span className={m.file ? 'bz-chat-bubble bz-chat-bubble-media' : 'bz-chat-bubble'}>
          {m.file ? (
            isImage ? (
              <ImageBubble
                convId={convId}
                file={m.file}
                localUrl={m.localUrl}
                sending={sending}
                progress={m.progress}
              />
            ) : (
              <FileCard
                file={m.file}
                mine={m.isMine}
                sending={sending}
                progress={m.progress}
                onSave={save}
                downloading={dl !== null}
                downloadProgress={dl ?? 0}
              />
            )
          ) : (
            <Linkified text={m.text} />
          )}
          <span className={m.file ? 'bz-chat-msg-time bz-chat-msg-time-over' : 'bz-chat-msg-time'}>
            {m.status === 'failed'
              ? 'Not sent'
              : new Date(m.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          {/* The arrow menu (Save, Copy) sits permanently at the image's
              top-right corner — WhatsApp style — never gated on hover. File
              cards keep their own tap-to-download affordance instead. */}
          {isImage && !sending && (
            <>
              <button
                type="button"
                className="bz-chat-menu-btn"
                onClick={toggleMenu}
                aria-label="Message actions"
              >
                <IconChevronDown size={14} />
              </button>
              {menuOpen && (
                <span className="bz-chat-msg-menu" role="menu">
                  <button type="button" className="bz-chat-menu-item" onClick={save}>
                    <IconDownload size={15} />
                    Save
                  </button>
                  {isImage && (
                    <button type="button" className="bz-chat-menu-item" onClick={copy}>
                      <IconCopy size={15} />
                      Copy
                    </button>
                  )}
                </span>
              )}
            </>
          )}
        </span>
        <button
          type="button"
          className="bz-chat-react-btn"
          onClick={togglePicker}
          aria-label="React"
        >
          <IconSmile size={14} />
        </button>
        {pickerOpen && (
          <span className="bz-chat-react-pop" role="menu" aria-label="React">
            {REACTION_SET.map((e) => (
              <ReactKey key={e} convId={convId} seq={m.seq} emoji={e} onDone={closePicker} />
            ))}
          </span>
        )}
        {m.reactions && m.reactions.length > 0 && (
          <span className="bz-chat-reactions">
            {m.reactions.map((r) => (
              <ReactionChip
                key={r.emoji}
                convId={convId}
                seq={m.seq}
                emoji={r.emoji}
                count={r.count}
                mine={r.mine}
              />
            ))}
          </span>
        )}
      </span>
    </div>
  );
});

function Thread({
  convId,
  name,
  peerId,
  peerAvatar,
  writable,
  pending,
}: {
  convId: string;
  name: string;
  peerId: string;
  peerAvatar?: string;
  writable: boolean;
  pending: boolean;
}) {
  const messages = useChatStore((s) => s.messages);
  const [draft, setDraft] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [reactFor, setReactFor] = useState<number | null>(null);
  // "Today" depends on the clock; the store stamps a snapshot whenever the
  // thread changes, so render never reads Date.now() itself.
  const now = useChatStore((s) => s.clock);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [plusOpen, setPlusOpen] = useState(false);
  const composeRef = useRef<HTMLElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const photoRef = useRef<HTMLInputElement>(null);
  const togglePlus = useCallback(() => setPlusOpen((v) => !v), []);
  const pickFile = useCallback(() => {
    setPlusOpen(false);
    fileRef.current?.click();
  }, []);
  const pickPhoto = useCallback(() => {
    setPlusOpen(false);
    photoRef.current?.click();
  }, []);
  const onFileChosen = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      void sendFile(convId, file).catch((err: unknown) =>
        pushToast(err instanceof Error ? `Could not send — ${err.message}` : 'Could not send file.', 'warning'),
      );
    },
    [convId],
  );

  // Both composer menus close like every popover: pick, Escape, or click-away.
  useEffect(() => {
    if (!emojiOpen && !plusOpen) return;
    const away = (e: MouseEvent) => {
      if (!composeRef.current?.contains(e.target as Node)) {
        setEmojiOpen(false);
        setPlusOpen(false);
      }
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEmojiOpen(false);
        setPlusOpen(false);
      }
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [emojiOpen, plusOpen]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, convId]);

  const send = useCallback(() => {
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    void sendMessage(convId, text).catch((err: unknown) => {
      pushToast(err instanceof Error ? `Could not send — ${err.message}` : 'Could not send.', 'warning');
    });
  }, [convId, draft]);
  const onDraftChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setDraft(e.target.value),
    [],
  );
  const onDraftKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        send();
      }
    },
    [send],
  );
  const back = useCallback(() => closeConversation(), []);
  const toggleEmoji = useCallback(() => setEmojiOpen((v) => !v), []);
  const pickEmoji = useCallback((emoji: string) => {
    const input = inputRef.current;
    // Insert at the caret, not the end — people fix up mid-sentence.
    setDraft((d) => {
      const at = input?.selectionStart ?? d.length;
      return d.slice(0, at) + emoji + d.slice(input?.selectionEnd ?? at);
    });
    input?.focus();
  }, []);

  return (
    <>
      <header className="bz-chat-head">
        <button type="button" className="bz-chat-back" onClick={back} aria-label="Back to discussions">
          ‹
        </button>
        <Avatar name={name} identity={peerId} size={34} photoUrl={peerAvatar} />
        <div className="bz-chat-head-body">
          <span className="bz-chat-head-name">{name}</span>
          <span className="bz-chat-head-sub">
            <IconLock size={11} />
            End-to-end encrypted
          </span>
        </div>
      </header>
      <div className="bz-chat-msgs">
        {messages.length === 0 &&
          (pending ? (
            <div className="bz-chat-blank" role="status" aria-label="Securing this conversation">
              <BrizinaLoader />
            </div>
          ) : (
            <div className="bz-chat-blank">
              <p>No messages yet. Say hello.</p>
            </div>
          ))}
        {messages.map((m, i) => (
          <Fragment key={m.clientId ?? m.seq}>
            {now > 0 && (i === 0 || crossesDay(messages[i - 1]?.at ?? m.at, m.at)) && (
              <div className="bz-chat-day">
                <span>{dayLabel(m.at, now)}</span>
              </div>
            )}
            <MessageRow convId={convId} m={m} pickerOpen={reactFor === m.seq} onPicker={setReactFor} />
          </Fragment>
        ))}
        <div ref={endRef} />
      </div>
      <footer className="bz-chat-compose" ref={composeRef}>
        <input ref={fileRef} type="file" hidden onChange={onFileChosen} />
        <input ref={photoRef} type="file" accept="image/*" hidden onChange={onFileChosen} />
        <IconButton
          shape="round"
          label="Share"
          className="bz-chat-sendbtn bz-chat-plusbtn"
          onClick={togglePlus}
          disabled={!writable || pending}
        >
          <IconPlus size={18} />
        </IconButton>
        {plusOpen && (
          <div className="bz-chat-plus-pop" role="menu" aria-label="Share">
            <button type="button" className="bz-chat-plus-item" onClick={pickPhoto}>
              <IconImage size={17} />
              Share photo
            </button>
            <button type="button" className="bz-chat-plus-item" onClick={pickFile}>
              <IconFiles size={17} />
              Share file
            </button>
          </div>
        )}
        <input
          ref={inputRef}
          className="bz-chat-input"
          value={draft}
          placeholder={pending ? '' : writable ? `Message ${name}` : 'You were removed from this conversation'}
          disabled={!writable || pending}
          onChange={onDraftChange}
          onKeyDown={onDraftKey}
        />
        {emojiOpen && (
          <div className="bz-chat-emoji-pop" role="menu" aria-label="Emoji">
            {QUICK_EMOJI.map((e) => (
              <EmojiKey key={e} emoji={e} onPick={pickEmoji} />
            ))}
          </div>
        )}
        <IconButton
          shape="round"
          label="Emoji"
          className="bz-chat-sendbtn bz-chat-emoji-sendlike"
          onClick={toggleEmoji}
          disabled={!writable || pending}
        >
          <IconSmile size={18} />
        </IconButton>
        <IconButton
          shape="round"
          label="Send"
          className="bz-chat-sendbtn"
          onClick={send}
          disabled={!writable || pending || !draft.trim()}
        >
          <IconSend size={16} />
        </IconButton>
      </footer>
    </>
  );
}
